// External Packages
import { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";

// Internal Packages
import { UtilityContext } from "../../context/util-context";
import Heading from "../UI/Heading";
import PaginationMenu from "../UI/PaginationMenu";
import DateFilter from "../UI/DateFilter";
import { requestAdminAPI } from "../../api/posh-api";
import LoadingCard from "../UI/LoadingCard";
import { isoDateToString } from "../../util/format-date";
import { endOfMonth, startOfMonth } from "date-fns";
import NoRecordsFound from "../UI/NoRecordsFound";
import ExportCsvButton from "../UI/ExportCsvButton";
import FilterMenu from "../UI/FilterMenu";
import { schema } from "../../util/schema";
import SearchBar from "../UI/SearchBar";
import AuditInfo from "./AuditInfo";

/** ========================================================================
 * * Audits Component
 * This component renders a list of actions performed by admin or customer.
 * ========================================================================== */

export default function Audits() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filter
  const defaultFilters = {
    sd: startOfMonth(new Date()).toISOString(),
    ed: endOfMonth(new Date()).toISOString(),
    table: "All",
    action: "All",
    agent: "All",
    query: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key]),
  );

  // Initialize States
  const [audits, setAudits] = useState([]);
  const [limit, setLimit] = useState(100);
  const [filter, setFilter] = useState(paramFilters);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      const data = await requestAdminAPI("search", {
        table: "audit",
        limit,
        filters: filter,
      });

      // Replace URL
      let url = "/audits?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setAudits(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== FILTER OPTIONS ===================
  const filterOptions = [
    {
      label: "Table",
      options: [
        "All",
        ...Object.keys(schema).filter(
          (key) => key !== "audit" && !/[A-Z]/.test(key),
        ),
      ],
      selected: filter.table,
    },
    {
      label: "Action",
      options: ["All", "create", "update", "delete"],
      selected: filter.action,
    },
    {
      label: "Agent",
      options: ["All", "employee", "customer", "partner"],
      selected: filter.agent,
    },
  ];

  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container gap-4">
      <AuditInfo open={!!selected} setOpen={setSelected} audit={selected} />
      <Heading
        title="Audits"
        text="Following is a list of actions performed by employees, customers and partners."
      >
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
          defaultPeriod="Month"
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <ExportCsvButton array={audits} filename="audits" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={5} max={8} />
      ) : audits.length > 0 ? (
        <div className="list">
          {audits.map((audit, idx) => (
            <div
              key={idx}
              className="grid lg:grid-cols-5 pill border-stone-600 text-stone-200"
              onClick={() => setSelected(audit)}
            >
              <p className="text-stone-400">
                {isoDateToString(audit.dateCreated, "MM/dd/yyyy hh:mm a")}
              </p>
              <p className="font-semibold">{audit.agent.name}</p>
              <p>{audit.action.toUpperCase()}</p>
              <p>{audit.table}</p>
              <p className="text-stone-400">{audit.row}</p>
            </div>
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}
      <PaginationMenu
        limit={limit}
        setLimit={setLimit}
        loading={loading}
        count={audits.length}
      />
    </div>
  );
}
